import React from 'react';
import { formatMonthName } from '../../../utils/dateFormatter';
import { formatSARGrouped } from '../../../utils/currencyFormatter';

/**
 * Six-month gross-revenue trend — hand-authored inline SVG.
 *
 * ── Why bars and not a line ──────────────────────────────────────────────────
 * These are six discrete monthly totals, not samples of a continuous quantity. A
 * line would draw slope between month-ends and invite the reader to interpolate a
 * value that does not exist. Bars state six magnitudes, which is the actual
 * question ("was July bigger than June?").
 *
 * ── RTL ──────────────────────────────────────────────────────────────────────
 * Time flows RIGHT to LEFT: oldest month on the right, the reported month on the
 * far left, matching how the rest of the page is read. The series arrives
 * oldest-first from the backend and is reversed here for painting only.
 *
 * ── Encoding ─────────────────────────────────────────────────────────────────
 * One series, so no legend — the card heading names it. Colour carries emphasis,
 * not identity: five history bars in a mid purple, the reported month in full
 * brand purple. Both clear 3:1 against the white card (validated: 4.22 and 11.66),
 * so neither depends on colour alone. Only the reported month is value-labelled —
 * a number on every bar is noise, and every figure is also in the cards above.
 * Each bar carries an SVG <title>, which is both the hover tooltip and what a
 * screen reader announces.
 *
 * No external chart library: this markup has to survive being printed to PDF
 * later (Phase 4), and inline SVG does.
 */

/** Full brand purple — the reported month. */
const COLOUR_CURRENT = '#471396';
/** Mid step of the same hue — the five history months. */
const COLOUR_HISTORY = '#8A6BC4';

// A fixed viewBox scaled by CSS: the chart is responsive without measuring the DOM.
//
// The 3.6:1 ratio is load-bearing. The SVG scales on WIDTH with `height: auto`, so
// the viewBox ratio alone decides the rendered height — capping height in CSS
// instead makes `preserveAspectRatio` letterbox the drawing and leaves the card
// two-thirds empty.
const VIEW_W = 720;
const VIEW_H = 200;
const PAD = { top: 24, bottom: 34, side: 10 };
const PLOT_H = VIEW_H - PAD.top - PAD.bottom;

/** Gap between bars, in viewBox units — the 2px surface gap at render size. */
const BAR_GAP = 18;

/**
 * Bar width ceiling, in viewBox units. Six bars sharing a wide card would each be
 * ~100 units, which reads as a block diagram rather than a chart; marks stay thin
 * and the slack becomes whitespace between them.
 */
const BAR_MAX_W = 40;

const RevenueTrendChart = ({ trend = [], currentMonth }) => {
  if (!Array.isArray(trend) || trend.length === 0) return null;

  // Painted right-to-left; the data stays oldest-first.
  const series = [...trend].reverse();

  const max = Math.max(...series.map(p => p.grossRevenue || 0));
  const slot = (VIEW_W - PAD.side * 2) / series.length;
  const barW = Math.min(Math.max(slot - BAR_GAP, 8), BAR_MAX_W);

  // Every month at zero is a real answer, not a broken chart.
  const allZero = max === 0;

  // ...but the answer is one sentence, so print the sentence and nothing else.
  // The drawing was still rendered at full height for an all-zero series, which on
  // paper is a large empty box above a caption — the emptiest page of the first
  // print sample. An axis with no data communicates less than the words do.
  if (allZero) {
    return (
      <div className="report-trend-chart is-empty">
        <p className="report-trend-caption">لا توجد إيرادات مسجلة في الأشهر الستة الماضية</p>
      </div>
    );
  }

  return (
    <div className="report-trend-chart">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        role="img"
        aria-label="اتجاه إجمالي إيرادات الحجوزات خلال آخر ستة أشهر"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Baseline only — a gridline per value would compete with the bars. */}
        <line
          x1={PAD.side} y1={PAD.top + PLOT_H}
          x2={VIEW_W - PAD.side} y2={PAD.top + PLOT_H}
          className="trend-baseline"
        />

        {series.map((point, i) => {
          const value = point.grossRevenue || 0;
          const h = Math.round((value / max) * PLOT_H);
          const x = PAD.side + i * slot + (slot - barW) / 2;
          const y = PAD.top + PLOT_H - h;
          const isCurrent = point.month === currentMonth;

          return (
            <g key={point.month}>
              <title>{`${formatMonthName(point.month)} — ${formatSARGrouped(value)}`}</title>

              {h > 0 && (
                <rect
                  x={x} y={y} width={barW} height={h}
                  rx="4"
                  fill={isCurrent ? COLOUR_CURRENT : COLOUR_HISTORY}
                />
              )}

              {/* Value on the reported month only. */}
              {isCurrent && (
                <text
                  x={x + barW / 2} y={Math.max(y - 8, 12)}
                  className="trend-value" textAnchor="middle"
                >
                  {value.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                </text>
              )}

              <text
                x={x + barW / 2} y={VIEW_H - 12}
                className={`trend-month${isCurrent ? ' is-current' : ''}`}
                textAnchor="middle"
              >
                {formatMonthName(point.month)}
              </text>
            </g>
          );
        })}
      </svg>

      <p className="report-trend-caption">
        إجمالي إيرادات الحجوزات لكل شهر · من اليمين إلى اليسار: الأقدم إلى الأحدث
      </p>
    </div>
  );
};

export default RevenueTrendChart;
